import React, { useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { cloneDeep, get as loGet } from 'lodash';
import localforage from 'localforage';

import { selectDataUser } from 'connect/selectors';
import { resetMatches } from 'connect/actions';

import { PARTNER_PAGE, LAST_UPDATE_KEY, CACHE_THRESHOLD } from './constants';
import { mapToRequestBody } from './helper';

export const PartnerContext = React.createContext();

export const PartnerMatchProvider = ({ children }) => {
  const dispatch = useDispatch();
  let storage = null;

  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  const user = useSelector(selectDataUser);

  // Fetch from indexedDb and store for use
  useEffect(() => {
    async function getStorage() {
      storage = await localforage.getItem(PARTNER_PAGE) || {};

      const lastUpdate = storage[LAST_UPDATE_KEY];
      const threshold = new Date().getTime() - CACHE_THRESHOLD;
      if (lastUpdate && lastUpdate.getTime() < threshold) {
        storage = {};
        dispatch(resetMatches());
      }

      setProgress(storage);
      setLoading(false);
    }

    getStorage();
  }, []);

  const save = (step, data, stepsToRemove) => {
    const merged = cloneDeep(progress);

    // "stepsToRemove" is used to remove steps on changes to do all operations in one
    if (stepsToRemove) {
      stepsToRemove.forEach(s => delete merged[s]);
    }

    merged[step] = {
      id: step,
      data,
    };
    merged[LAST_UPDATE_KEY] = new Date();

    setProgress(merged);
    localforage.setItem(PARTNER_PAGE, merged);
  };

  const get = (step, key) => {
    const found = loGet(progress, step, {});

    if (key) return found[key];

    // Default to data
    return found.data;
  };

  // Clears both local & cached data (immediate clear)
  const clear = () => {
    setProgress({});
    clearCache();
  };

  // Clears cache only (cleared on reload)
  const clearCache = () => {
    localforage.setItem(PARTNER_PAGE, {});
  };

  const isEmpty = optionalExcludedSteps => {
    if (!loading && Object.keys(progress).length > 0) {
      const storageMinusExcludedSteps = Object.keys(progress).filter(
        step => (optionalExcludedSteps || []).indexOf(step) === -1,
      );

      return storageMinusExcludedSteps.length === 0;
    }

    return !loading && Object.keys(progress).length === 0;
  };

  const transform = () => mapToRequestBody(user, progress);

  return (
    <PartnerContext.Provider
      value={{
        loading,
        progress,
        save,
        get,
        clear,
        clearCache,
        isEmpty,
        transform,
      }}
    >
      {children}
    </PartnerContext.Provider>
  );
};

PartnerMatchProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePartnerStorage = () => useContext(PartnerContext);
